"use client"

import IconArrowLeft from "@/app/(site)/_components/Atoms/Icons/IconArrowLeft"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import { isSafari } from "@/lib/siteconfig/constants"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import {
    Children,
    useCallback,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactNode
} from "react"

type StaticFrom = "sm" | "md" | "lg"
type ControlsFrom = "sm" | "md" | "lg"
type StaticColumns = 2 | 3 | 4

const STATIC_FROM_TRACK: Record<StaticFrom, string> = {
    sm: "sm:!w-full sm:snap-none sm:overflow-visible sm:pb-0 sm:pr-0",
    md: "md:!w-full md:snap-none md:overflow-visible md:pb-0 md:pr-0",
    lg: "lg:!w-full lg:snap-none lg:overflow-visible lg:pb-0 lg:pr-0"
}

const STATIC_FROM_FLEX_GAP: Record<StaticFrom, string> = {
    sm: "sm:gap-x-24",
    md: "md:gap-x-24",
    lg: "lg:gap-x-24"
}

const STATIC_FROM_ITEM: Record<StaticFrom, string> = {
    sm: "sm:min-w-0 sm:snap-none sm:self-auto",
    md: "md:min-w-0 md:snap-none md:self-auto",
    lg: "lg:min-w-0 lg:snap-none lg:self-auto"
}

const LAST_ITEM_SECTION_PADDING =
    "last:box-content last:pr-12 last:temp-md:pr-16 last:lg:pr-24"

const STATIC_FROM_LAST_ITEM_PAD_RESET: Record<StaticFrom, string> = {
    sm: "sm:!box-border sm:!pr-0",
    md: "md:!box-border md:!pr-0",
    lg: "lg:!box-border lg:!pr-0"
}

/**
 * The last slide can never reach the start edge, so it snaps to the end
 * instead. Its `box-content` end padding is part of the snap area, which
 * lines the card up with the section padding at the true end of the scroll.
 */
const LAST_ITEM_SNAP_END = "last:snap-end"

const STATIC_FROM_TRACK_SECTION_PADDING: Record<StaticFrom, string> = {
    sm: "sm:!pr-12 temp-md:!pr-16 lg:!pr-24",
    md: "md:!pr-16 lg:!pr-24",
    lg: "lg:!pr-24"
}

const STATIC_FROM_ITEM_GRID: Record<StaticFrom, string> = {
    sm: "sm:!w-full sm:shrink",
    md: "md:!w-full md:shrink",
    lg: "lg:!w-full lg:shrink"
}

const STATIC_FROM_ITEM_FLEX: Record<StaticFrom, string> = {
    sm: "sm:!w-auto sm:flex-1",
    md: "md:!w-auto md:flex-1",
    lg: "lg:!w-auto lg:flex-1"
}

const STATIC_FROM_CONTROLS: Record<StaticFrom, string> = {
    sm: "sm:hidden",
    md: "md:hidden",
    lg: "lg:hidden"
}

const CONTROLS_FROM: Record<ControlsFrom, string> = {
    sm: "hidden sm:flex",
    md: "hidden md:flex",
    lg: "hidden lg:flex"
}

const STATIC_GRID_COLS: Record<StaticFrom, Record<StaticColumns, string>> = {
    sm: {
        2: "sm:grid sm:grid-cols-2",
        3: "sm:grid sm:grid-cols-3",
        4: "sm:grid sm:grid-cols-4"
    },
    md: {
        2: "md:grid md:grid-cols-2",
        3: "md:grid md:grid-cols-3",
        4: "md:grid md:grid-cols-4"
    },
    lg: {
        2: "lg:grid lg:grid-cols-2",
        3: "lg:grid lg:grid-cols-3",
        4: "lg:grid lg:grid-cols-4"
    }
}

const MOBILE_MEDIA_QUERY = "(max-width: 639px)"
const TABLET_MEDIA_QUERY = "(min-width: 640px) and (max-width: 1023px)"
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"
const MOBILE_GAP_PX = 12
const TABLET_GAP_PX = 16
const DESKTOP_GAP_PX = 24
const NEXT_CARD_PEEK_PX = 72
const MOBILE_CARD_WIDTH_WITH_NEXT_PEEK = "70%"
const TABLET_SINGLE_CARD_WIDTH_WITH_NEXT_PEEK = "57%"
const ARROW_HIT_AREA =
    "relative before:absolute before:inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']"

const getScrollBehavior = (): ScrollBehavior => {
    if (typeof window === "undefined") return "smooth"
    return window.matchMedia(REDUCED_MOTION_QUERY).matches ? "auto" : "smooth"
}

const progressWidth = (progress: number) => `${Math.max(20, progress * 80 + 20)}%`

export type CarouselViewport = "mobile" | "tablet" | "desktop"

/**
 * Widths come from media queries so the first paint matches the phone.
 * SSR cannot see the viewport, and a desktop width that later changes to
 * the mobile width leaves iOS scroll-snap parked mid-slide.
 */
const getCarouselItemWidth = ({
    viewport,
    itemsPerView,
    totalItems,
    lastItemOnEdge,
    itemWidths
}: {
    viewport: CarouselViewport
    itemsPerView: number
    totalItems: number
    lastItemOnEdge?: boolean
    itemWidths?: Partial<Record<CarouselViewport, string>>
}): string => {
    const itemWidthOverride = itemWidths?.[viewport]
    if (itemWidthOverride) return itemWidthOverride

    const shouldPeek = Boolean(lastItemOnEdge) && totalItems > itemsPerView
    const gap =
        viewport === "mobile"
            ? MOBILE_GAP_PX
            : viewport === "tablet"
              ? TABLET_GAP_PX
              : DESKTOP_GAP_PX

    if (viewport === "mobile") {
        if (itemsPerView === 1) {
            return shouldPeek ? MOBILE_CARD_WIDTH_WITH_NEXT_PEEK : "97%"
        }
        return `calc((100% - ${(itemsPerView - 1) * gap}px) / ${itemsPerView})`
    }

    if (shouldPeek) {
        if (viewport === "tablet" && itemsPerView === 1) {
            return TABLET_SINGLE_CARD_WIDTH_WITH_NEXT_PEEK
        }
        return `calc((100% - ${(itemsPerView - 1) * gap}px - ${NEXT_CARD_PEEK_PX}px) / ${itemsPerView})`
    }

    return `calc((100% - ${(itemsPerView - 1) * gap}px) / ${itemsPerView})`
}

interface GCCarouselSliderProps {
    children: ReactNode
    itemsPerView?: number
    itemsPerViewMobile?: number
    itemsPerViewTablet?: number
    lastItemOnEdge?: boolean
    /** CSS width per viewport; overrides the width derived from itemsPerView. */
    itemWidths?: Partial<Record<CarouselViewport, string>>
    showControls?: boolean
    /** Hide arrows and progress bar below this breakpoint. */
    showControlsFrom?: ControlsFrom
    className?: string
    trackClassName?: string
    itemClassName?: string
    controlsClassName?: string
    /** End inset on the last slide, matching the showcase section padding. */
    padLastItem?: boolean
    /** From this breakpoint up, CSS disables scroll and controls. */
    staticFrom?: StaticFrom
    staticLayout?: "flex" | "grid"
    staticColumns?: StaticColumns
}

const useCarouselViewport = (): CarouselViewport => {
    const [viewport, setViewport] = useState<CarouselViewport>("desktop")

    useLayoutEffect(() => {
        const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
        const tabletQuery = window.matchMedia(TABLET_MEDIA_QUERY)

        const syncViewport = () => {
            if (mobileQuery.matches) {
                setViewport("mobile")
                return
            }

            if (tabletQuery.matches) {
                setViewport("tablet")
                return
            }

            setViewport("desktop")
        }

        syncViewport()
        mobileQuery.addEventListener("change", syncViewport)
        tabletQuery.addEventListener("change", syncViewport)

        return () => {
            mobileQuery.removeEventListener("change", syncViewport)
            tabletQuery.removeEventListener("change", syncViewport)
        }
    }, [])

    return viewport
}

export default function GCCarouselSlider({
    children,
    itemsPerView: itemsPerViewProp = 3,
    itemsPerViewMobile,
    itemsPerViewTablet,
    lastItemOnEdge,
    itemWidths,
    showControls = true,
    showControlsFrom,
    className,
    trackClassName,
    itemClassName,
    controlsClassName,
    padLastItem,
    staticFrom,
    staticLayout = "flex",
    staticColumns = 4
}: GCCarouselSliderProps) {
    const viewport = useCarouselViewport()
    const isMobile = viewport === "mobile"
    const isTablet = viewport === "tablet"
    const langCode = useLangCode()
    const sliderRef = useRef<HTMLDivElement>(null)
    const progressFillRef = useRef<HTMLDivElement>(null)
    const currentIndexRef = useRef(0)
    const scrollProgressRef = useRef(0)
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)

    const childrenArray = Children.toArray(children)
    const totalItems = childrenArray.length
    const isStaticGrid = Boolean(staticFrom) && staticLayout === "grid"
    const isPagedScroll = staticFrom === "sm" ? isMobile : isMobile || isTablet

    const itemsPerView = isMobile
        ? itemsPerViewMobile || 1
        : isTablet
          ? itemsPerViewTablet || 2
          : itemsPerViewProp || 3

    const shouldShowControls = showControls && totalItems > itemsPerView

    const getSlideStride = useCallback(() => {
        const slider = sliderRef.current
        if (!slider) return 0

        const first = slider.children[0] as HTMLElement | undefined
        const second = slider.children[1] as HTMLElement | undefined
        if (first && second) {
            return second.offsetLeft - first.offsetLeft
        }

        return first?.offsetWidth ?? slider.clientWidth
    }, [])

    const handleScroll = useCallback(() => {
        const slider = sliderRef.current
        if (!slider) return

        const scrollLeft = slider.scrollLeft
        const scrollWidth = slider.scrollWidth
        const clientWidth = slider.clientWidth

        setIsAtStart(scrollLeft === 0)
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)

        const maxScroll = scrollWidth - clientWidth
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
        scrollProgressRef.current = progress

        if (progressFillRef.current) {
            progressFillRef.current.style.width = progressWidth(progress)
        }

        // 1-up slides are < 100% wide so a peek of the next card is visible.
        const stride = getSlideStride()
        if (isPagedScroll && stride > 0) {
            currentIndexRef.current = Math.round(scrollLeft / stride)
        }
    }, [getSlideStride, isPagedScroll])

    const scrollToPage = (direction: "prev" | "next") => {
        const slider = sliderRef.current
        if (!slider) return

        const behavior = getScrollBehavior()

        if (isPagedScroll) {
            const delta = direction === "next" ? 1 : -1
            const newIndex = Math.min(
                totalItems - 1,
                Math.max(0, currentIndexRef.current + delta)
            )
            currentIndexRef.current = newIndex
            slider.scrollTo({
                left: newIndex * getSlideStride(),
                behavior
            })
            return
        }

        const scrollAmount = getSlideStride() || slider.clientWidth * (1 / itemsPerView)
        slider.scrollBy({
            left: direction === "next" ? scrollAmount : -scrollAmount,
            behavior
        })
    }

    const handleResize = useCallback(() => {
        handleScroll()
    }, [handleScroll])

    useLayoutEffect(() => {
        if (typeof window === "undefined") return

        handleScroll()

        const slider = sliderRef.current
        if (slider) {
            slider.addEventListener("scroll", handleScroll, { passive: true })
        }
        window.addEventListener("resize", handleResize, { passive: true })

        return () => {
            if (slider) {
                slider.removeEventListener("scroll", handleScroll)
            }
            window.removeEventListener("resize", handleResize)
        }
    }, [handleResize, handleScroll, totalItems])

    const mobileItemsPerView = itemsPerViewMobile || 1
    const tabletItemsPerView = itemsPerViewTablet || 2
    const desktopItemsPerView = itemsPerViewProp || 3
    const widthArgs = {
        totalItems,
        lastItemOnEdge,
        itemWidths
    }
    const mobileItemWidth = getCarouselItemWidth({
        ...widthArgs,
        viewport: "mobile",
        itemsPerView: mobileItemsPerView
    })
    const tabletItemWidth = getCarouselItemWidth({
        ...widthArgs,
        viewport: "tablet",
        itemsPerView: tabletItemsPerView
    })
    const desktopItemWidth = getCarouselItemWidth({
        ...widthArgs,
        viewport: "desktop",
        itemsPerView: desktopItemsPerView
    })
    const carouselId = useId().replace(/:/g, "")
    // A percentage kept in a custom property on the track is resolved by Safari
    // against the wrong box. Every slide then gets the wrong width, and image
    // ratios that depend on that width follow it. The width has to be a real
    // `width` on the slide.
    const slideWidthCss = `
[data-carousel="${carouselId}"] > [data-carousel-slide] {
    width: ${mobileItemWidth};
}
@media (min-width: 640px) {
    [data-carousel="${carouselId}"] > [data-carousel-slide] {
        width: ${tabletItemWidth};
    }
}
@media (min-width: 1024px) {
    [data-carousel="${carouselId}"] > [data-carousel-slide] {
        width: ${desktopItemWidth};
    }
}
`

    // Safari keeps a stray scroll offset from before hydration, then mandatory
    // snap will not return to the first slide. Chrome and Firefox recover
    // without this. Snap has to stay off until the write has landed — putting
    // it back in the same turn makes Safari jump to the bad offset again.
    useLayoutEffect(() => {
        if (!isSafari) return

        const slider = sliderRef.current
        if (!slider) return

        let userInteracted = false
        let restoreTimer = 0
        const markInteracted = () => {
            userInteracted = true
        }

        slider.addEventListener("pointerdown", markInteracted, { passive: true })
        slider.addEventListener("wheel", markInteracted, { passive: true })
        slider.addEventListener("keydown", markInteracted)

        const correctStrayOffset = () => {
            if (userInteracted || slider.scrollLeft < 1) return

            window.clearTimeout(restoreTimer)
            slider.style.scrollSnapType = "none"
            void slider.offsetWidth
            slider.scrollLeft = 0
            restoreTimer = window.setTimeout(() => {
                slider.style.scrollSnapType = ""
            }, 50)
        }

        correctStrayOffset()
        const frame = requestAnimationFrame(correctStrayOffset)
        const later = window.setTimeout(correctStrayOffset, 300)

        return () => {
            cancelAnimationFrame(frame)
            window.clearTimeout(later)
            window.clearTimeout(restoreTimer)
            slider.style.scrollSnapType = ""
            slider.removeEventListener("pointerdown", markInteracted)
            slider.removeEventListener("wheel", markInteracted)
            slider.removeEventListener("keydown", markInteracted)
        }
    }, [])

    const handleTrackKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target instanceof HTMLInputElement) return
        if (event.target instanceof HTMLTextAreaElement) return

        if (event.key === "ArrowLeft") {
            event.preventDefault()
            scrollToPage("prev")
        }
        if (event.key === "ArrowRight") {
            event.preventDefault()
            scrollToPage("next")
        }
    }

    return (
        <div
            className={clsx("relative", className)}
            role="region"
            aria-roledescription="carousel"
            aria-label="Carousel"
        >
            <style>{slideWidthCss}</style>
            <div
                ref={sliderRef}
                data-carousel={carouselId}
                role="region"
                className={clsx(
                    "no-scrollbar flex snap-x snap-mandatory overflow-x-auto pb-16 [overflow-anchor:none] lg:pb-32",
                    lastItemOnEdge
                        ? "w-full"
                        : "w-[calc(100%+12px)] sm:w-[calc(100%+16px)] lg:w-full",
                    !lastItemOnEdge && "pr-12 sm:pr-16 lg:pr-0",
                    staticFrom && STATIC_FROM_TRACK[staticFrom],
                    padLastItem &&
                        staticFrom &&
                        STATIC_FROM_TRACK_SECTION_PADDING[staticFrom],
                    staticFrom && !isStaticGrid && STATIC_FROM_FLEX_GAP[staticFrom],
                    isStaticGrid &&
                        staticFrom &&
                        STATIC_GRID_COLS[staticFrom][staticColumns],
                    trackClassName
                )}
                aria-label={t(langCode, "common.carousel")}
                tabIndex={0}
                onKeyDown={handleTrackKeyDown}
            >
                {childrenArray.map((child, index) => (
                    <div
                        key={index}
                        data-carousel-slide=""
                        className={clsx(
                            "shrink-0 snap-start self-start",
                            padLastItem && LAST_ITEM_SECTION_PADDING,
                            padLastItem && LAST_ITEM_SNAP_END,
                            padLastItem &&
                                staticFrom &&
                                STATIC_FROM_LAST_ITEM_PAD_RESET[staticFrom],
                            staticFrom && STATIC_FROM_ITEM[staticFrom],
                            staticFrom &&
                                (isStaticGrid
                                    ? STATIC_FROM_ITEM_GRID[staticFrom]
                                    : STATIC_FROM_ITEM_FLEX[staticFrom]),
                            itemClassName
                        )}
                    >
                        {child}
                    </div>
                ))}
            </div>
            {shouldShowControls ? (
                <div
                    className={clsx(
                        "items-center justify-between gap-8",
                        showControlsFrom ? CONTROLS_FROM[showControlsFrom] : "flex",
                        staticFrom && STATIC_FROM_CONTROLS[staticFrom],
                        controlsClassName
                    )}
                >
                    <div
                        className="h-1 w-[70%] overflow-hidden rounded-full bg-subtle lg:w-1/2"
                        role="progressbar"
                        aria-label="Carousel scroll progress"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(scrollProgressRef.current * 100)}
                    >
                        <div
                            ref={progressFillRef}
                            className="h-full bg-main-black"
                            style={{
                                width: progressWidth(scrollProgressRef.current)
                            }}
                        />
                    </div>

                    <div className="flex gap-8 p-5">
                        <button
                            type="button"
                            className={clsx(
                                ARROW_HIT_AREA,
                                "text-main-black",
                                FOCUS_VISIBLE_CLASS,
                                HIT_TARGET_CLASS,
                                isAtStart && "text-main-light-grey-disabled"
                            )}
                            disabled={isAtStart}
                            onClick={() => scrollToPage("prev")}
                            aria-label={t(langCode, "common.previous_item")}
                        >
                            <IconArrowLeft aria-hidden="true" className="h-16 w-16" />
                        </button>
                        <button
                            type="button"
                            className={clsx(
                                ARROW_HIT_AREA,
                                "text-main-black",
                                FOCUS_VISIBLE_CLASS,
                                HIT_TARGET_CLASS,
                                isAtEnd && "text-main-light-grey-disabled"
                            )}
                            disabled={isAtEnd}
                            onClick={() => scrollToPage("next")}
                            aria-label={t(langCode, "common.next_item")}
                        >
                            <IconArrowRight aria-hidden="true" className="h-16 w-16" />
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    )
}
